import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  NOTIFICATION_GROUPS,
  NOTIFICATION_KINDS,
  NOTIFICATION_KIND_MAP,
  NOTIFICATION_PRIORITY_MAP,
  REMINDER_LEAD_OPTIONS,
  allPreferences,
  reminderLabel,
  type NotificationKind,
  type NotificationPreferences,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { dateTime, relative } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { useTenantProfile } from '../../lib/settings';
import { Icon, type IconName } from '../../components/Icon';
import type { NotificationRow } from '../../components/Notifications';
import {
  Badge,
  EmptyState,
  ErrorState,
  PageHead,
  Pager,
  TableSkeleton,
} from '../../components/ui';

interface Page_ {
  items: NotificationRow[];
  total: number;
  unread: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/* ==========================================================================
   كل الإشعارات
   ========================================================================== */

export function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [kind, setKind] = useState('');

  const query = { page, pageSize: 30, unreadOnly: unreadOnly || undefined, kind: kind || undefined };

  const list = useQuery({
    queryKey: ['notifications', 'page', query],
    queryFn: () => api<Page_>(`/notifications${qs(query)}`),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['notifications'] });

  const markAll = useMutation({
    mutationFn: () => api('/notifications/read-all', { method: 'POST' }),
    onSuccess: refresh,
  });

  const dismissAll = useMutation({
    mutationFn: () => api('/notifications/all', { method: 'DELETE' }),
    onSuccess: refresh,
  });

  const markRead = useMutation({
    mutationFn: (ids: string[]) => api('/notifications/read', { method: 'POST', body: { ids } }),
    onSuccess: refresh,
  });

  const rows = list.data?.items ?? [];

  return (
    <div className="page">
      <PageHead
        title="الإشعارات"
        subtitle={list.data ? `${list.data.unread} غير مقروء من ${list.data.total}` : undefined}
        actions={
          <>
            <button className="btn" onClick={() => navigate('/settings/notifications')}>
              التفضيلات
            </button>
            {(list.data?.unread ?? 0) > 0 && (
              <button className="btn" onClick={() => markAll.mutate()}>
                تعليم الكل مقروءًا
              </button>
            )}
            {rows.length > 0 && (
              <button className="btn btn-danger" onClick={() => dismissAll.mutate()}>
                إخفاء الكل
              </button>
            )}
          </>
        }
      />

      <div className="toolbar">
        <select className="select" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">كل الأنواع</option>
          {NOTIFICATION_KINDS.map((k) => (
            <option key={k} value={k}>
              {NOTIFICATION_KIND_MAP[k].label}
            </option>
          ))}
        </select>

        <label className="check">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => {
              setUnreadOnly(e.target.checked);
              setPage(1);
            }}
          />
          <span>غير المقروء فقط</span>
        </label>
      </div>

      {list.isError && <ErrorState error={list.error} onRetry={() => list.refetch()} />}

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>الإشعار</th>
                <th>النوع</th>
                <th>الأولوية</th>
                <th>الوقت</th>
              </tr>
            </thead>

            {list.isLoading && <TableSkeleton rows={8} cols={4} />}

            <tbody>
              {rows.map((n) => (
                <tr
                  key={n.id}
                  style={{ cursor: n.link ? 'pointer' : undefined }}
                  onClick={() => {
                    if (!n.readAt) markRead.mutate([n.id]);
                    if (n.link) navigate(n.link);
                  }}
                >
                  <td>
                    <span className={n.readAt ? 'cell-mute' : 'cell-strong'}>{n.title}</span>
                    {n.body && <div className="tiny mute">{n.body}</div>}
                  </td>
                  <td className="cell-mute">{NOTIFICATION_KIND_MAP[n.kind]?.label ?? n.kind}</td>
                  <td>
                    <Badge kind={NOTIFICATION_PRIORITY_MAP[n.priority].kind}>
                      {NOTIFICATION_PRIORITY_MAP[n.priority].label}
                    </Badge>
                  </td>
                  <td className="num cell-mute" title={dateTime(n.createdAt)}>
                    {relative(n.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!list.isLoading && rows.length === 0 && (
          <EmptyState
            title="لا إشعارات"
            text="يصلك هنا ما يستحق انتباهك: مواعيدك ومهامك وما يتأخر من فواتير."
          />
        )}

        {list.data && (
          <Pager
            page={page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={setPage}
          />
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   التفضيلات
   ========================================================================== */

/* أيقونةٌ لكلِّ مجموعةِ إشعاراتٍ — لإكسابِ الأقسامِ طابعًا بصريًّا. */
const GROUP_ICONS: Record<string, IconName> = {
  المواعيد: 'calendar',
  'العمل اليومي': 'briefcase',
  المهام: 'target',
  المال: 'wallet',
  'الموارد البشرية': 'users',
  الحساب: 'settings',
};
const groupIcon = (g: string): IconName => GROUP_ICONS[g] ?? 'bell';

/** مفتاحُ تبديلٍ أنيق (بديلُ مربّعِ الاختيار) للإشعارات. */
function Toggle({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className={`switch${disabled ? ' is-disabled' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="switch-track">
        <span className="switch-thumb" />
      </span>
    </label>
  );
}

export function NotificationSettings() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const profile = useTenantProfile();

  const [prefs, setPrefs] = useState<NotificationPreferences>({});
  const [lead, setLead] = useState('15');
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  /* الأقسامُ مفتوحةٌ افتراضيًّا، وكلٌّ يُطوى ويُفتح بالنقرِ على ترويسته. */
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  /* مهلةُ التذكير: خياراتٌ جاهزةٌ أو مدّةٌ مخصّصةٌ يحدّدها المستخدم. */
  const [customMode, setCustomMode] = useState(false);
  const [leadUnit, setLeadUnit] = useState<'min' | 'hr'>('min');

  const canSetLead = atLeast('ADMIN');

  const saved = useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: () => api<NotificationPreferences>('/notifications/preferences'),
  });

  useEffect(() => {
    if (loaded || !saved.data) return;
    setPrefs(saved.data);
    setLoaded(true);
  }, [saved.data, loaded]);

  useEffect(() => {
    const s = profile.data?.settings as Record<string, unknown> | undefined;
    if (s && typeof s.meetingReminderMinutes === 'number' && !dirty) {
      setLead(String(s.meetingReminderMinutes));
    }
  }, [profile.data, dirty]);

  const save = useMutation({
    mutationFn: async () => {
      await api('/notifications/preferences', { method: 'PATCH', body: prefs });
      /*
       * مهلة التذكير إعداد شركة لا تفضيل شخصي.
       *
       * الموعد واحد ويحضره أكثر من واحد؛ لو كانت لكل مستخدم مهلته لوصل
       * التذكير في أوقات مختلفة عن الاجتماع نفسه، وهو ارتباك لا مرونة.
       */
      if (canSetLead) {
        await api('/settings/preferences', {
          method: 'PATCH',
          body: { meetingReminderMinutes: Number(lead) || 0 },
        });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      setDirty(false);
    },
  });

  const set = (kind: NotificationKind, patch: { enabled?: boolean; popup?: boolean }) => {
    const current = allPreferences(prefs).find((p) => p.key === kind)!;
    setPrefs((p) => ({
      ...p,
      [kind]: {
        enabled: patch.enabled ?? current.enabled,
        popup: patch.popup ?? current.popup,
      },
    }));
    setDirty(true);
  };

  if (saved.isError) return <ErrorState error={saved.error} onRetry={() => saved.refetch()} />;

  const all = allPreferences(prefs);

  /* حالةُ اختيارِ المهلة: قيمةٌ جاهزةٌ، أو «بلا»، أو مخصّصة. */
  const leadNum = Number(lead) || 0;
  const leadIsPreset = (REMINDER_LEAD_OPTIONS as readonly number[]).includes(leadNum);
  const customActive = customMode || (leadNum > 0 && !leadIsPreset);
  const choosePreset = (m: number) => {
    setCustomMode(false);
    setLead(String(m));
    setDirty(true);
  };
  const chooseNone = () => {
    setCustomMode(false);
    setLead('0');
    setDirty(true);
  };
  const chooseCustom = () => {
    setCustomMode(true);
    if (leadNum === 0) setLead('20');
    setDirty(true);
  };
  const customDisplay =
    leadNum > 0 ? (leadUnit === 'hr' ? Math.round((leadNum / 60) * 100) / 100 : leadNum) : '';
  const onCustomNum = (raw: string) => {
    const n = Number(raw.replace(/[^\d.]/g, '')) || 0;
    const mins = Math.round(leadUnit === 'hr' ? n * 60 : n);
    setLead(String(Math.max(0, Math.min(1440, mins))));
    setDirty(true);
  };

  return (
    <div className="page">
      {/* الترويسةُ وزرُّ الحفظِ ثابتان — يبدأُ التمريرُ من الأقسامِ تحتهما. */}
      <div className="page-sticky">
        <PageHead
          title="تفضيلات الإشعارات"
          subtitle="ما يصلك، وما ينبثق أمامك"
          actions={
            <button
              className="btn btn-primary"
              onClick={() => save.mutate()}
              disabled={!dirty || save.isPending}
            >
              {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
            </button>
          }
        />
      </div>

      {save.isSuccess && !dirty && (
        <div className="alert alert-info mb-4">
          <Icon name="check" size={15} />
          <div>حُفظت التفضيلات.</div>
        </div>
      )}

      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      {/* التذكيرُ بالمواعيد — بطاقةٌ بترويسةٍ متدرّجةٍ وأيقونة */}
      <div className="ntf-lead card">
        <div className="ntf-lead-head">
          <span className="ntf-lead-ic">
            <Icon name="clock" size={20} />
          </span>
          <div>
            <h2 className="ntf-lead-title">التذكير بالمواعيد</h2>
            <span className="tiny mute">متى يصلك التنبيه قبل الموعد</span>
          </div>
        </div>
        <div className="card-body">
          <span className="label">يصلك التنبيه قبل الموعد بـ</span>
          <div className="lead-chips">
            {REMINDER_LEAD_OPTIONS.map((m) => (
              <button
                type="button"
                key={m}
                className={`lead-chip${!customActive && leadNum === m ? ' is-on' : ''}`}
                onClick={() => choosePreset(m)}
                disabled={!canSetLead}
              >
                {reminderLabel(m).replace('قبل ', '')}
              </button>
            ))}
            <button
              type="button"
              className={`lead-chip${leadNum === 0 && !customMode ? ' is-on' : ''}`}
              onClick={chooseNone}
              disabled={!canSetLead}
            >
              بلا تذكير
            </button>
            <button
              type="button"
              className={`lead-chip lead-chip-custom${customActive ? ' is-on' : ''}`}
              onClick={chooseCustom}
              disabled={!canSetLead}
            >
              <Icon name="edit" size={13} /> مخصّص
            </button>
          </div>

          {customActive && (
            <div className="lead-custom">
              <input
                className="input num lead-custom-num"
                dir="ltr"
                inputMode="numeric"
                value={customDisplay}
                onChange={(e) => onCustomNum(e.target.value)}
                disabled={!canSetLead}
                aria-label="المدة المخصّصة"
              />
              <div className="lead-unit">
                <button
                  type="button"
                  className={`lead-unit-opt${leadUnit === 'min' ? ' is-on' : ''}`}
                  onClick={() => setLeadUnit('min')}
                  disabled={!canSetLead}
                >
                  دقيقة
                </button>
                <button
                  type="button"
                  className={`lead-unit-opt${leadUnit === 'hr' ? ' is-on' : ''}`}
                  onClick={() => setLeadUnit('hr')}
                  disabled={!canSetLead}
                >
                  ساعة
                </button>
              </div>
              <span className="lead-custom-hint">
                = {reminderLabel(leadNum)} · الحدُّ الأقصى يومٌ واحد (1440 دقيقة)
              </span>
            </div>
          )}

          <span className="tiny mute lead-note">
            {canSetLead
              ? 'إعداد على مستوى الشركة: الموعد واحد ويحضره أكثر من شخص، فيصلهم التنبيه معًا.'
              : 'يضبطه مدير النظام — الموعد واحد ويحضره أكثر من شخص.'}
          </span>

          <div className="alert alert-info mt-4">
            <Icon name="info" size={15} />
            <div>
              التنبيه يظهر منبثقًا أمامك ما دام النظام مفتوحًا، ويبقى في الجرس إن لم تكن أمامه.
              يفحص النظام المواعيد كل دقيقة.
            </div>
          </div>
        </div>
      </div>

      {NOTIFICATION_GROUPS.map((group) => {
        const items = all.filter((p) => p.group === group);
        const onCount = items.filter((p) => p.enabled).length;
        const isOpen = !collapsed[group];
        return (
          <section className={`ntf-group card mt-4${isOpen ? ' is-open' : ''}`} key={group}>
            <button
              type="button"
              className="ntf-group-head"
              onClick={() => setCollapsed((c) => ({ ...c, [group]: isOpen }))}
              aria-expanded={isOpen}
            >
              <span className="ntf-group-ic">
                <Icon name={groupIcon(group)} size={18} />
              </span>
              <div className="ntf-group-meta">
                <h2 className="ntf-group-title">{group}</h2>
                <span className="tiny mute">
                  {onCount} من {items.length} مُفعّل
                </span>
              </div>
              <span className="ntf-group-right">
                <span className="ntf-count">
                  {onCount}/{items.length}
                </span>
                <Icon name="chevronDown" size={16} />
              </span>
            </button>

            {isOpen && (
              <div className="ntf-rows">
                <div className="ntf-rows-head">
                  <span>الإشعار</span>
                  <span className="ntf-col">
                    <Icon name="bell" size={13} /> يصلني
                  </span>
                  <span className="ntf-col">
                    <Icon name="zap" size={13} /> ينبثق أمامي
                  </span>
                </div>
                {items.map((p) => (
                  <div className="ntf-row" key={p.key}>
                    <div className="ntf-row-main">
                      <strong>{p.label}</strong>
                      <span className="tiny mute">{p.description}</span>
                    </div>
                    <div className="ntf-cell">
                      <Toggle
                        checked={p.enabled}
                        disabled={p.mandatory}
                        onChange={(v) => set(p.key, { enabled: v })}
                        label={`${p.label} — يصلني`}
                      />
                      {p.mandatory && <span className="ntf-lock">لا يُطفأ</span>}
                    </div>
                    <div className="ntf-cell">
                      <Toggle
                        checked={p.popup}
                        disabled={!p.enabled}
                        onChange={(v) => set(p.key, { popup: v })}
                        label={`${p.label} — ينبثق أمامي`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      })}

      <p className="tiny mute mt-4">
        إشعارات المال والصلاحيات لا تُطفأ: فاتورة متأخرة أو تغيّر في دورك أمر يخصّ الشركة لا
        تفضيلًا شخصيًا.
      </p>
    </div>
  );
}
